import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Link as LinkIcon, Upload } from "lucide-react";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import { toast } from "sonner";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import DocumentDetailsDrawer from "@/components/knowledge-base/details-drawer/DocumentDetailsDrawer";
import { GenieDocumentCard } from "@/components/knowledge-base/GenieDocumentCard";
import { SearchableKnowledgeBase } from "@/components/knowledge-base/SearchableKnowledgeBase";
import UploadNewSection from "@/components/knowledge-base/UploadNewSection";
import {
  useDeleteDocument,
  useRemoveDocumentFromKB,
  useSendDocumentToElevenLabs,
  useUpdateDocument,
} from "@/hooks/documents/useDocumentMutations";
import { useKnowledgeBaseToggle } from "@/hooks/documents/useKnowledgeBaseToggle";
import { cn } from "@/lib/utils";
import { DocumentService } from "@/services/api/DocumentService";
import { Agent } from "@/types/agent/agent";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface KnowledgeDrawerProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
}

type TabType = "upload" | "documents";

export const KnowledgeDrawer = ({ genie, isOpen, onClose }: KnowledgeDrawerProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("upload");
  const [selectedDocument, setSelectedDocument] = useState<DocumentWithMetadata | null>(null);
  const [isTogglingDocument, setIsTogglingDocument] = useState(false);

  const hasKnowledgeBase = (genie.knowledge_base?.length ?? 0) > 0;
  const { data: fetchedDocuments = [] } = useDocuments("my-documents", { agentId: genie.id }, undefined, isOpen && !hasKnowledgeBase);

  const knowledgeBaseDocuments = useMemo<DocumentWithMetadata[]>(
    () => hasKnowledgeBase ? (genie.knowledge_base as DocumentWithMetadata[]) : fetchedDocuments,
    [genie.knowledge_base, fetchedDocuments, hasKnowledgeBase],
  );

  const deleteDocumentMutation = useDeleteDocument();
  const updateDocumentMutation = useUpdateDocument();
  const sendToElevenLabsMutation = useSendDocumentToElevenLabs();
  const removeFromKBMutation = useRemoveDocumentFromKB();

  const handleUpdateDocument = async (document: DocumentWithMetadata, updates: Partial<DocumentWithMetadata>) => {
    const updatedDoc = await updateDocumentMutation.mutateAsync({ document, updates });
    if (selectedDocument?.id === document.id && updatedDoc) setSelectedDocument(updatedDoc);
  };

  const handleDeleteDocument = async (id: string) => {
    deleteDocumentMutation.mutate(id);
    setSelectedDocument(null);
  };

  const handleViewDocument = async (document: DocumentWithMetadata) => {
    const pathToUse = document.source_path || document.parsed_path;
    if (pathToUse) {
      const signedUrl = await DocumentService.getSignedUrl(pathToUse);
      if (signedUrl) window.open(signedUrl, "_blank");
    }
  };

  const selectedDocuments = useMemo(
    () => genie?.settings?.conversation_config?.agent?.prompt?.knowledge_base || [],
    [genie?.settings?.conversation_config?.agent?.prompt?.knowledge_base],
  );

  const { toggleDocuments } = useKnowledgeBaseToggle({ onSuccess: () => setIsTogglingDocument(false) });

  const handleRemoveFromKB = useCallback(
    async (doc: DocumentWithMetadata) => {
      await removeFromKBMutation(doc);
      // Sync genie settings so selectedDocuments stays accurate
      await toggleDocuments(genie, doc, false);
    },
    [genie, removeFromKBMutation, toggleDocuments],
  );

  const handleKnowledgeBaseToggle = useCallback(
    async (doc: DocumentWithMetadata, checked: boolean) => {
      setIsTogglingDocument(true);
      try {
        await toggleDocuments(genie, doc, checked);
      } catch {
        // Error already handled by hook
      }
    },
    [genie, toggleDocuments],
  );

  const handleBulkToggle = useCallback(
    async (docs: DocumentWithMetadata[], checked: boolean) => {
      setIsTogglingDocument(true);
      const toastId = toast.loading(
        checked ? `Linking ${docs.length} documents...` : `Unlinking ${docs.length} documents...`,
      );
      try {
        await toggleDocuments(genie, docs, checked);
        toast.success(checked ? `Linked ${docs.length} documents` : `Unlinked ${docs.length} documents`, {
          id: toastId,
        });
      } catch {
        toast.error("Failed to update knowledge base", { id: toastId });
      } finally {
        setIsTogglingDocument(false);
      }
    },
    [genie, toggleDocuments],
  );

  return (
    <>
      <CoreDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={`Knowledge for ${genie.name}`}
        description="Add PDFs and resources your genie can cite from. Linked documents become part of the genie's knowledge base and improve factual accuracy."
      >
        <div className="flex items-center justify-between border-b border-gray-200 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("upload")}
              className={cn(
                "px-6 py-2 font-semibold text-sm transition-all duration-200 border-b-2 -mb-px",
                activeTab === "upload"
                  ? "text-genie-primary border-genie-primary"
                  : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300",
              )}
            >
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload New
              </div>
            </button>
            <button
              onClick={() => setActiveTab("documents")}
              className={cn(
                "px-6 py-2 font-semibold text-sm transition-all duration-200 border-b-2 -mb-px",
                activeTab === "documents"
                  ? "text-genie-primary border-genie-primary"
                  : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300",
              )}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Genie Documents
                {knowledgeBaseDocuments.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-genie-primary/10 text-genie-primary font-bold">
                    {knowledgeBaseDocuments.length}
                  </span>
                )}
              </div>
            </button>
          </div>
          <div className="mb-1 p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md hover:shadow-lg hover:scale-105 transition-all">
            <button
              onClick={() => navigate("/genie-hub/knowledge-base")}
              className="px-3 py-1.5 text-sm font-semibold rounded-[10px] bg-white hover:bg-gray-50 transition-colors text-gray-700 w-full"
            >
              Open full Knowledge
            </button>
          </div>
        </div>

        {activeTab === "upload" && (
          <UploadNewSection
            defaultAgentId={genie.id}
            availableAgents={[{ id: genie.id, elevenlabs_id: genie.elevenlabs_id, name: genie.name, branding: genie.branding }]}
          />
        )}

        {activeTab === "documents" && (
          <div className="space-y-6">
            {knowledgeBaseDocuments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium text-gray-900 mb-2">No documents linked yet</p>
                <p className="text-sm text-gray-600">Link existing documents below</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {knowledgeBaseDocuments.map((doc) => (
                  <GenieDocumentCard
                    key={doc.id}
                    document={doc}
                    onClick={() => setSelectedDocument(doc)}
                    onView={() => handleViewDocument(doc)}
                    onEdit={() => setSelectedDocument(doc)}
                    onDetach={() => handleRemoveFromKB(doc)}
                    showActions={true}
                  />
                ))}
              </div>
            )}

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <LinkIcon className="h-5 w-5 text-genie-primary" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Link Existing Documents</h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Search and link documents from your Knowledge Base to this genie
                  </p>
                </div>
              </div>
              <SearchableKnowledgeBase
                selectedDocuments={selectedDocuments}
                onDocumentToggle={handleKnowledgeBaseToggle}
                onBulkToggle={handleBulkToggle}
                maxHeight="max-h-64"
                disabled={isTogglingDocument}
                ownerId={genie.user_id}
              />
            </div>
          </div>
        )}
      </CoreDrawer>

      <DocumentDetailsDrawer
        document={selectedDocument}
        isOpen={!!selectedDocument}
        onClose={() => setSelectedDocument(null)}
        onSendToKB={sendToElevenLabsMutation}
        onRemoveFromKB={handleRemoveFromKB}
        onDocumentUpdate={handleUpdateDocument}
        onDocumentDelete={handleDeleteDocument}
      />
    </>
  );
};

export default KnowledgeDrawer;
